import { useState } from 'react';
import {
  ActivityLogIcon,
  ArchiveIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DotsHorizontalIcon,
  FileTextIcon,
  MagnifyingGlassIcon,
  PaperPlaneIcon,
  Pencil2Icon,
  PlusIcon,
  ReaderIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import type { EbookClient } from '../services/ebooks';
import { EbookShelfCover } from './EbookShelfCover';
import { textbookProgress } from '../services/textbook-progress';
import { TextbookQuestionTotal } from './TextbookQuestionTotal';
import './teacher-textbook-shelf.css';

type TextbookShelf = Awaited<ReturnType<EbookClient['list']>>;
type Textbook = TextbookShelf[number];

export interface TeacherTextbookShelfProps {
  readonly client: EbookClient;
  readonly items: TextbookShelf;
  readonly busy: boolean;
  readonly error: string;
  readonly onOpen: (id: string) => void;
  readonly onDelete: (item: Textbook) => void;
  readonly onImport: () => void;
  readonly onRefresh: () => void;
  readonly onTotalSaved: (result: Awaited<ReturnType<EbookClient['setQuestionTotal']>>) => void;
}

const workflow = [
  { title: '页面识别', description: '识别 PDF 页面内容', icon: FileTextIcon },
  { title: '题目校对', description: '确认棋形与答案变化', icon: Pencil2Icon },
  { title: '教材题库', description: '整理已校对的题目', icon: ArchiveIcon },
  { title: '编排与预览', description: '调整顺序与版式', icon: ReaderIcon },
  { title: '发布电子书', description: '发布给学生练习', icon: PaperPlaneIcon },
  { title: '学习记录', description: '查看学生练习情况', icon: ActivityLogIcon },
] as const;

function coverPath(item: Textbook) {
  return item.coverImage ? `/documents/${item.id}/covers/${item.coverImage}` : null;
}

function updatedDate(value: string) {
  return new Date(value).toLocaleDateString('zh-CN');
}

export function TeacherTextbookShelf({
  client,
  items,
  busy,
  error,
  onOpen,
  onDelete,
  onImport,
  onRefresh,
  onTotalSaved,
}: TeacherTextbookShelfProps) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('recent');
  const [page, setPage] = useState(0);
  const [selection, setSelection] = useState<string | null>(null);
  const matching = items
    .filter((item) => item.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()))
    .sort((a, b) =>
      sort === 'name'
        ? a.name.localeCompare(b.name, 'zh-CN')
        : Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
    );
  const pages = Math.max(1, Math.ceil(matching.length / 6));
  const currentPage = Math.min(page, pages - 1);
  const visibleItems = matching.slice(currentPage * 6, currentPage * 6 + 6);
  // A refreshed, filtered, or paged list must never show details for an invisible book.
  const selected = visibleItems.find((item) => item.id === selection) ?? visibleItems[0];
  const progress = selected ? textbookProgress(selected) : null;

  const moreActions = (item: Textbook, detail = false) => (
    <details
      className="teacher-textbook-shelf__more"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          event.currentTarget.removeAttribute('open');
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.currentTarget.removeAttribute('open');
        event.currentTarget.querySelector('summary')?.focus();
      }}
    >
      <summary
        aria-label={detail ? `当前教材操作：${item.name}` : `教材操作：${item.name}`}
        aria-disabled={busy}
        onClick={(event) => {
          if (busy) event.preventDefault();
        }}
      >
        <DotsHorizontalIcon aria-hidden="true" />
      </summary>
      <div className="teacher-textbook-shelf__menu">
        <button
          type="button"
          disabled={busy}
          aria-label={detail ? `删除当前教材：${item.name}` : `删除教材：${item.name}`}
          onClick={(event) => {
            event.currentTarget.closest('details')?.removeAttribute('open');
            onDelete(item);
          }}
        >
          删除教材
        </button>
      </div>
    </details>
  );

  return (
    <section className="teacher-textbook-shelf" aria-label="我的教材">
      <header className="teacher-textbook-shelf__heading">
        <div>
          <h1 id="teacher-textbook-heading" tabIndex={-1}>
            我的教材
          </h1>
          <p>管理教材，准备每一堂练习。</p>
        </div>
        <button
          type="button"
          className="teacher-textbook-shelf__primary"
          onClick={onImport}
          disabled={busy}
        >
          <PlusIcon aria-hidden="true" />
          导入教材
        </button>
      </header>

      {error && (
        <div className="teacher-textbook-shelf__error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={onRefresh} disabled={busy}>
            <ReloadIcon aria-hidden="true" />
            重新加载
          </button>
        </div>
      )}

      <div className="teacher-textbook-shelf__layout">
        <section className="teacher-textbook-shelf__catalog" aria-label="教材目录" aria-busy={busy}>
          <div className="teacher-textbook-shelf__filters">
            <label className="teacher-textbook-shelf__search">
              <MagnifyingGlassIcon aria-hidden="true" />
              <input
                type="search"
                aria-label="按教材名称搜索"
                placeholder="搜索教材名称"
                value={query}
                disabled={busy}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(0);
                  setSelection(null);
                }}
              />
            </label>
            <select
              aria-label="教材排序"
              value={sort}
              disabled={busy}
              onChange={(event) => {
                setSort(event.target.value);
                setPage(0);
                setSelection(null);
              }}
            >
              <option value="recent">最近修改</option>
              <option value="name">教材名称</option>
            </select>
          </div>

          {visibleItems.length > 0 ? (
            <ul className="teacher-textbook-shelf__list">
              {visibleItems.map((item) => (
                <li key={item.id} className={selected?.id === item.id ? 'is-selected' : undefined}>
                  <button
                    type="button"
                    className="teacher-textbook-shelf__select"
                    aria-label={`选择教材：${item.name}`}
                    aria-pressed={selected?.id === item.id}
                    disabled={busy}
                    onClick={() => setSelection(item.id)}
                  >
                    <EbookShelfCover client={client} path={coverPath(item)} title={item.name} />
                    <span className="teacher-textbook-shelf__item-text">
                      <strong>{item.name}</strong>
                      <span>
                        {item.pageCount} 页 PDF · 更新于 {updatedDate(item.updatedAt)}
                      </span>
                    </span>
                  </button>
                  {moreActions(item)}
                </li>
              ))}
            </ul>
          ) : (
            <div className="teacher-textbook-shelf__empty" role="status">
              <ReaderIcon aria-hidden="true" />
              <strong>
                {busy
                  ? '正在加载教材…'
                  : error
                    ? '教材暂未加载'
                    : query.trim()
                      ? '没有找到匹配的教材'
                      : '导入第一本教材'}
              </strong>
              <p>
                {busy
                  ? '请稍候。'
                  : error
                    ? '请重新加载后查看教材目录。'
                    : query.trim()
                      ? '试试其他关键词，或清空搜索。'
                      : '从 PDF 开始，制作适合学生的围棋练习。'}
              </p>
              {!busy && !error && query.trim() && (
                <button type="button" onClick={() => setQuery('')}>
                  清空搜索
                </button>
              )}
            </div>
          )}

          {visibleItems.length > 0 && (
            <footer className="teacher-textbook-shelf__pagination">
              <span>
                {query.trim() ? `找到 ${matching.length} 本教材` : `共 ${items.length} 本教材`}
              </span>
              {pages > 1 && (
                <nav aria-label="教材目录分页">
                  <button
                    type="button"
                    aria-label="上一页教材"
                    disabled={busy || currentPage === 0}
                    onClick={() => {
                      setPage(currentPage - 1);
                      setSelection(null);
                    }}
                  >
                    <ChevronLeftIcon aria-hidden="true" />
                  </button>
                  <span aria-live="polite">
                    {currentPage + 1} / {pages}
                  </span>
                  <button
                    type="button"
                    aria-label="下一页教材"
                    disabled={busy || currentPage === pages - 1}
                    onClick={() => {
                      setPage(currentPage + 1);
                      setSelection(null);
                    }}
                  >
                    <ChevronRightIcon aria-hidden="true" />
                  </button>
                </nav>
              )}
            </footer>
          )}
        </section>

        <section className="teacher-textbook-shelf__detail" aria-label="教材详情" aria-busy={busy}>
          {selected ? (
            <>
              {moreActions(selected, true)}
              <div className="teacher-textbook-shelf__book">
                <EbookShelfCover client={client} path={coverPath(selected)} title={selected.name} />
                <div className="teacher-textbook-shelf__book-title">
                  <h2>{selected.name}</h2>
                  <p>
                    {selected.pageCount} 页 PDF · 更新于 {updatedDate(selected.updatedAt)}
                  </p>
                  <TextbookQuestionTotal
                    key={selected.id}
                    client={client}
                    item={selected}
                    busy={busy}
                    onSaved={onTotalSaved}
                    onRefresh={onRefresh}
                  />
                </div>
                {progress && (
                  <div
                    className="teacher-textbook-shelf__progress"
                    role="group"
                    aria-label="编排完成度"
                  >
                    <span className="teacher-textbook-shelf__progress-label">编排完成度</span>
                    <strong className="teacher-textbook-shelf__percentage">
                      {progress.percentage ?? '—'}
                      {progress.percentage !== null && <span>%</span>}
                    </strong>
                    <span className="teacher-textbook-shelf__progress-count">{progress.count}</span>
                    {progress.totalMissing && (
                      <span className="teacher-textbook-shelf__progress-note">总题数未设置</span>
                    )}
                  </div>
                )}
              </div>
              <div className="teacher-textbook-shelf__workflow">
                <h3>制作流程</h3>
                <ol aria-label="教材制作流程">
                  {workflow.map(({ title, description, icon: Icon }) => (
                    <li key={title}>
                      <span className="teacher-textbook-shelf__step-icon">
                        <Icon aria-hidden="true" />
                      </span>
                      <span>
                        <strong>{title}</strong>
                        <span>{description}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
              <footer className="teacher-textbook-shelf__continue">
                <p>从页面识别开始，进入教材制作工作台。</p>
                <button
                  type="button"
                  className="teacher-textbook-shelf__primary"
                  disabled={busy}
                  onClick={() => onOpen(selected.id)}
                >
                  继续制作
                  <ArrowRightIcon aria-hidden="true" />
                </button>
              </footer>
            </>
          ) : (
            <div className="teacher-textbook-shelf__detail-placeholder">
              <ReaderIcon aria-hidden="true" />
              <h2>{busy ? '正在准备教材目录' : '教材制作工作台'}</h2>
              <p>
                {error
                  ? '加载教材后，在这里查看教材信息。'
                  : query.trim()
                    ? '搜索并选择教材，查看制作流程。'
                    : '导入教材后，即可识别页面、校对题目并发布给学生。'}
              </p>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
